import { AiOutlineCloseCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { Box, Button, Flex, Icon, Progress, Text } from "@chakra-ui/react";
import { array, object, string } from "yup";
import { useFieldArray, useForm } from "react-hook-form";

import PrimaryButton from "components/Button/PrimaryButton";
import { yupResolver } from "@hookform/resolvers/yup";
import CardDefault from "ui/components/Cards/CardDefault";
import Field from "ui/components/Input/Field";
import useInstrucoesBoleto from "core/features/instrucao-boleto/hooks/useInstrucoesBoleto";
import { useEffect } from "react";
import { TipoInstrucaoBoletoEnum } from "core/features/instrucao-boleto/enums";
import useSaveInstrucaoBoleto from "core/features/instrucao-boleto/hooks/useSaveInstrucaoBoleto";
import { InstrucaoBoletoResponse } from "core/features/instrucao-boleto/typings";
import { showErrorAxios, showSuccess } from "infra/helpers/alerta";

const MAX_LINES = 5;

const validationSchema = object().shape({
  bills: array()
    .of(
      object().shape({
        text: string().required("Este campo não pode ser vazio."),
      })
    )
    .required(),
  agreements: array()
    .of(
      object().shape({
        text: string().required("Este campo não pode ser vazio."),
      })
    )
    .required(),
});

type FormInstrucoes = {
  bills: { instrucao: string }[];
  agreements: { instrucao: string }[];
};

export default function InstrucoesBoleto({ tenantId }: { tenantId: string }) {
  const labels: string[] = [
    "Primeira",
    "Segunda",
    "Terceira",
    "Quarta",
    "Quinta",
  ];

  const { data, isLoading } = useInstrucoesBoleto(tenantId);
  const { mutate: handleSave, isLoading: isLoadingSave } =
    useSaveInstrucaoBoleto(tenantId);

  const { control, watch } = useForm<FormInstrucoes>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      bills: [],
      agreements: [],
    },
  });

  // Fields arrays
  const {
    fields: billLines,
    append: appendBills,
    remove: removeBills,
  } = useFieldArray({ control, name: "bills" });

  const {
    fields: agreementLines,
    append: appendAgreements,
    remove: removeAgreements,
  } = useFieldArray({ control, name: "agreements" });

  useEffect(() => {
    if (data) {
      const _bills = data
        .filter((item) => item.tipo === TipoInstrucaoBoletoEnum.Normal)
        .map((item) => ({ instrucao: item.instrucao }));

      _bills.forEach((item) => {
        appendBills(item);
      });

      const agreements = data
        .filter((item) => item.tipo === TipoInstrucaoBoletoEnum.Acordo)
        .map((item) => ({ instrucao: item.instrucao }));

      agreements.forEach((item) => {
        appendAgreements(item);
      });
    }
  }, [data, appendBills, appendAgreements]);

  // Toggle inputs
  const appendBillsInput = () => {
    if (billLines.length < MAX_LINES) {
      appendBills({ instrucao: "" });
    }
  };

  const removeBillsInput = (id: number) => {
    removeBills(id);
  };

  const appendAgreementsInput = () => {
    if (agreementLines.length < MAX_LINES) {
      appendAgreements({ instrucao: "" });
    }
  };

  const removeAgreementsInput = (id: number) => {
    removeAgreements(id);
  };

  // Submit
  const onSubmit = (data: FormInstrucoes) => {
    let body: InstrucaoBoletoResponse[] = [];

    if (data.bills.length) {
      body = [
        ...data.bills.map(
          (item, index): InstrucaoBoletoResponse => ({
            tipo: TipoInstrucaoBoletoEnum.Normal,
            instrucao: item.instrucao,
            ordem: index,
          })
        ),
      ];
    }

    if (data.agreements.length) {
      body = [
        ...body,
        ...data.agreements.map(
          (item, index): InstrucaoBoletoResponse => ({
            tipo: TipoInstrucaoBoletoEnum.Acordo,
            instrucao: item.instrucao,
            ordem: data.bills.length + index,
          })
        ),
      ];
    }

    body = body
      .filter((item) => !!item.instrucao)
      .map((item, index) => ({ ...item, ordem: index }));

    handleSave(body, {
      onSuccess: () => {
        showSuccess();
      },
      onError: (error) => {
        showErrorAxios(error);
      },
    });
  };

  return (
    <CardDefault>
      {isLoading ? (
        <Progress
          size="sm"
          isIndeterminate
          w="100%"
          my={4}
          colorScheme="cyan"
        />
      ) : null}

      <Flex alignItems="center" mb={"16px"}>
        <Text mr={2} fontSize="18px" fontWeight="bold">
          Instruções adicionais de textos para boletos
        </Text>

        <Button
          aria-label="Adicionar campo"
          isDisabled={billLines.length >= MAX_LINES}
          onClick={appendBillsInput}
          variant={"ghost"}
        >
          <Icon as={AiOutlinePlusCircle} boxSize={8} />
        </Button>
      </Flex>
      {billLines.map((bill, billIndex) => (
        <Box key={bill.id} mb={2}>
          <Flex>
            <Flex flex={1}>
              <Field
                label={`${labels[billIndex]} linha`}
                control={control}
                name={`bills.${billIndex}.instrucao`}
                inputProps={{
                  maxLength: 80,
                }}
                rules={{
                  maxLength: {
                    value: 80,
                    message: "O campo deve ter no máximo 80 caracteres",
                  },
                }}
              />
            </Flex>
            <Flex pl="16px" pt="18px">
              <Button
                variant="ghost"
                onClick={() => removeBillsInput(billIndex)}
              >
                <Icon color="red" as={AiOutlineCloseCircle} boxSize={6} />
              </Button>
            </Flex>
          </Flex>
        </Box>
      ))}
      <Flex alignItems="center" my="16px">
        <Text mr={2} fontSize="18px" fontWeight="bold">
          Instruções adicionais de textos para acordos
        </Text>
        <Button
          aria-label="Adicionar campo"
          isDisabled={agreementLines.length >= MAX_LINES}
          onClick={appendAgreementsInput}
          variant={"ghost"}
        >
          <Icon as={AiOutlinePlusCircle} boxSize={8} />
        </Button>
      </Flex>
      {agreementLines.map((agreement, agreementIndex) => (
        <Box key={agreement.id} mb={2}>
          <Flex>
            <Flex flex={1}>
              <Field
                label={`${labels[agreementIndex]} linha`}
                control={control}
                name={`agreements.${agreementIndex}.instrucao`}
                inputProps={{
                  maxLength: 80,
                }}
                rules={{
                  maxLength: {
                    value: 80,
                    message: "O campo deve ter no máximo 80 caracteres",
                  },
                }}
              />
            </Flex>
            <Flex pl="16px" pt="18px">
              <Button
                variant="ghost"
                onClick={() => removeAgreementsInput(agreementIndex)}
              >
                <Icon color="red" as={AiOutlineCloseCircle} boxSize={6} />
              </Button>
            </Flex>
          </Flex>
        </Box>
      ))}
      <Flex mt={8} justifyContent="right">
        <PrimaryButton
          isLoading={isLoadingSave}
          isDisabled={isLoadingSave}
          onClick={() => {
            onSubmit({
              agreements: watch("agreements"),
              bills: watch("bills"),
            });
          }}
        >
          Salvar Alterações
        </PrimaryButton>
      </Flex>
    </CardDefault>
  );
}
